import { configureChildApp } from '@rendora/common/utils/appContext';
import type { ModalProps } from '@rendora/design/components/modal/index';
import Modal from '@rendora/design/components/modal/Modal.vue';

import { createApp, nextTick, provide, ref, type Component, type VNode } from 'vue';
import Confirm from './Confirm.vue';

// Modal 配置接口
export interface ModalConfig extends Partial<ModalProps> {
  content?: Component | VNode;
  extra?: Component | VNode;
  header?: Component | VNode;
  onCancel?: () => void;
}

// Confirm 配置接口
export interface ConfirmConfig {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  type?: 'warning' | 'info' | 'success' | 'error';
  width?: string | number;
  maxWidth?: string | number;
}

// Modal 实例接口
export interface ModalInstance {
  close: () => void;
  update: (config: Partial<ModalConfig>) => void;
}

// 基础 Modal 工厂类
export class ModalFactory {
  private static instances = new Map<symbol, ModalInstance>();
  private static zIndexCounter = 901;
  private static readonly MAX_Z_INDEX = 1000;

  static getInstance(symbol: symbol): ModalInstance | undefined {
    return ModalFactory.instances.get(symbol);
  }

  // 类型守卫
  private static isValidModalInstance(
    instance: ModalInstance | undefined,
  ): instance is ModalInstance {
    return instance !== undefined && typeof instance.close === 'function';
  }

  // 创建基础 Modal
  static create(config: ModalConfig, symbol?: symbol): ModalInstance {
    const modalSymbol = symbol || Symbol('modal');
    const visible = ref(false);

    // 防止 z-index 溢出，重置为初始值
    if (this.zIndexCounter >= this.MAX_Z_INDEX) {
      this.zIndexCounter = 900;
    }

    const currentConfig = ref<ModalConfig>({
      zIndex: ++this.zIndexCounter,
      ...config,
    });

    // 检查是否已存在相同的 modal
    if (this.instances.has(modalSymbol)) {
      // eslint-disable-next-line no-console
      console.warn('Modal with this symbol already exists, replacing it');
      const existingInstance = this.instances.get(modalSymbol);
      if (this.isValidModalInstance(existingInstance)) {
        existingInstance.close();
      }
    }

    // 创建 Vue app 的挂载容器
    // 注意：Modal 组件使用 Teleport to="body"，实际内容会传送到 body
    // 这个 wrapper 只用于挂载 Vue app，Modal 的 DOM 会通过 Teleport 到 body
    const modalWrapper = document.createElement('div');
    document.body.appendChild(modalWrapper);

    // 提前声明 app 变量，避免闭包问题
    let app: ReturnType<typeof createApp> | null = null;

    const modalInstance: ModalInstance = {
      close: () => {
        visible.value = false;
        setTimeout(() => {
          try {
            // 确保应用正确卸载
            if (app) {
              app.unmount();
              app = null;
            }
            // 确保 DOM 节点被移除
            if (modalWrapper?.parentNode) {
              modalWrapper.parentNode.removeChild(modalWrapper);
            }
            // 从实例映射中删除
            ModalFactory.instances.delete(modalSymbol);
            // 调用取消回调
            currentConfig.value.onCancel?.();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to close modal:', error);
            // 即使出错也要清理实例
            ModalFactory.instances.delete(modalSymbol);
          }
        }, 200);
      },
      update: (newConfig: Partial<ModalConfig>) => {
        try {
          Object.assign(currentConfig.value, newConfig);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update modal config:', error);
        }
      },
    };

    ModalFactory.instances.set(modalSymbol, modalInstance);

    // 创建 Vue 应用
    app = createApp({
      setup() {
        const handleClose = () => {
          modalInstance.close();
        };

        provide('modalInstance', modalInstance);

        const slots = {
          extra: currentConfig.value.extra ? () => currentConfig.value.extra : undefined,
          default: currentConfig.value.content ? () => currentConfig.value.content : undefined,
          header: currentConfig.value.header ? () => currentConfig.value.header : undefined,
        };

        return () => (
          <Modal
            modelValue={visible.value}
            {...{
              'onUpdate:modelValue': (val: boolean) => {
                visible.value = val;
                if (!val) {
                  handleClose();
                }
              },
            }}
            maskStyle={currentConfig.value.maskStyle}
            border={currentConfig.value.border}
            background={currentConfig.value.background}
            padding={currentConfig.value.padding}
            borderRadius={currentConfig.value.borderRadius}
            title={currentConfig.value.title}
            width={currentConfig.value.width}
            height={currentConfig.value.height}
            maxHeight={currentConfig.value.maxHeight}
            maxWidth={currentConfig.value.maxWidth}
            showHeader={currentConfig.value.showHeader}
            showCloseButton={currentConfig.value.showCloseButton}
            closeOnOverlayClick={currentConfig.value.closeOnOverlayClick}
            closeOnEsc={currentConfig.value.closeOnEsc}
            maskClosable={currentConfig.value.maskClosable}
            zIndex={currentConfig.value.zIndex}
            centered={currentConfig.value.centered}
            fullscreen={currentConfig.value.fullscreen}
            minWidth={currentConfig.value.minWidth}
            boxShadow={currentConfig.value.boxShadow}
            onClose={handleClose}
            v-slots={slots}
          >
            {/* {currentConfig.value.content} */}
          </Modal>
        );
      },
    });

    // 配置模态框应用 - 继承主应用的 i18n、指令等
    configureChildApp(app);

    // 挂载应用
    // Modal 组件会通过 Teleport 将内容传送到 body，wrapper 只作为 Vue app 的挂载点
    app.mount(modalWrapper);

    // 显示动画
    nextTick(() => {
      visible.value = true;
    });

    return modalInstance;
  }

  // 清除所有 modal
  static clear() {
    try {
      this.instances.forEach((instance, symbol) => {
        try {
          if (this.isValidModalInstance(instance)) {
            instance.close();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to close modal instance:', symbol.toString(), error);
        }
      });
      this.instances.clear();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to clear modals:', error);
      // 即使出错也要重置状态
      this.instances.clear();
    }
  }

  // 获取当前活跃的 modal 数量
  static getActiveModalCount(): number {
    return this.instances.size;
  }

  // 检查是否存在指定的 modal
  static hasModal(symbol: symbol): boolean {
    return this.instances.has(symbol);
  }

  // 创建确认对话框
  static confirm(config: ConfirmConfig): ModalInstance {
    const { onOk, onCancel, ...restConfig } = config;
    const { width, maxWidth, ...confirmProps } = restConfig;

    // 创建 modal 实例
    const modalInstance = this.create({
      content: (
        <Confirm
          {...confirmProps}
          onOk={onOk}
          onCancel={onCancel}
          onClose={() => modalInstance.close()}
        />
      ),
      width: width ?? '500px',
      maxWidth,
      showHeader: false,
      showCloseButton: true,
      closeOnOverlayClick: false,
      closeOnEsc: true,
      maskClosable: false,
      centered: true,
      borderRadius: '8px',
      padding: '0',
    });

    return modalInstance;
  }
}
